import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, deleteDoc, doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import useUserData from "../hooks/useUserData";
import { SiteStateContext } from "./SiteStateContext";
import { db } from "./firebase";
import { getDungeonIntelMap } from "./dgIntel";
import dungeonSeedData from "./dungeons.json";

const UNIVERSE_URL = "/universe.json";
const UNIVERSE_CACHE_KEY = "ss:universe-cache";
const UNIVERSE_CACHE_TTL_MS = 10 * 60 * 1000;
const UNIVERSE_REFRESH_MS = 15 * 60 * 1000;
const DG_NAME_PATTERN = /^DG\s+(.+)\s+(\d+)\.(\d+)([A-Z]?)$/;
const DG_COLLAPSE_STORAGE_KEY = "ss:dg-collapsed-layers";
const DG_EDIT_URL = "https://imp.slypandainteractive.com/update_dg_intel_field.php";
const createEmptyEditValues = () => ({ monsterType: "", boss: "", guards: "" });
const isBossLevelLabel = (label) => /^0\./.test(String(label || "").trim());
const LAYER_DEFS = [
{ id: "earthforce", title: "Earthforce", layer: 0 },
{ id: "wildspace", title: "Wildspace", layer: 3 },
{ id: "perilous-space", title: "Perilous Space", layer: 4 },
{ id: "kalthi-depths", title: "Kalthi Depths", layer: 6 }];

const createEmptyLayers = () =>
LAYER_DEFS.map((layer) => ({
  ...layer,
  dungeons: []
}));

const readUniverseCache = () => {
  try {
    const raw = localStorage.getItem(UNIVERSE_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.timestamp || !parsed?.data) return null;
    if (Date.now() - parsed.timestamp > UNIVERSE_CACHE_TTL_MS) return null;
    return parsed;
  } catch (err) {
    return null;
  }
};

const writeUniverseCache = (data, timestamp) => {
  try {
    localStorage.setItem(
      UNIVERSE_CACHE_KEY,
      JSON.stringify({
        timestamp,
        data
      })
    );
  } catch (err) {

  }
};

const buildUniverseGraph = (data) => {
  const adjacency = {};
  const nameToId = {};
  const idToName = {};
  const galaxies = data?.galaxies ? Object.values(data.galaxies) : [];

  galaxies.forEach((galaxy) => {
    const id = String(galaxy?.ID ?? "");
    if (!id) return;
    const name = String(galaxy?.name ?? "").trim();

    if (name) nameToId[name.toLowerCase()] = id;
    idToName[id] = name || id;

    adjacency[id] = Array.isArray(galaxy?.links) ? galaxy.links.map((link) => String(link)) : [];
  });

  return { adjacency, nameToId, idToName, galaxies };
};

const buildFlagKey = (galaxy, dgCode) =>
`${String(galaxy || "").trim().toLowerCase()}::${String(dgCode || "").trim().toLowerCase()}`;

const buildFlagDocId = (galaxy, dgCode) => encodeURIComponent(buildFlagKey(galaxy, dgCode));

const formatPercent = (mapped, total) => {
  if (!total || total <= 0) return { value: 0, label: "0.0%" };
  const raw = (mapped / total) * 100;
  const value = Number.isFinite(raw) ? raw : 0;
  return { value, label: `${value.toFixed(1)}%` };
};

const MappedStatChip = ({ label, mapped, total }) => {
  const safeMapped = Math.max(0, mapped || 0);
  const safeTotal = Math.max(0, total || 0);
  const { value, label: percentLabel } = formatPercent(safeMapped, safeTotal);
  const left = Math.max(safeTotal - safeMapped, 0);
  const fillWidth = value > 0 ? Math.max(2, Math.min(100, value)) : 0;

  return (
    <div
      className="dgs-stat-chip"
      style={{ "--fill-width": `${fillWidth}%` }}
      aria-label={`${label} mapped ${safeMapped} of ${safeTotal} (${percentLabel})`}
    >
      <div className="dgs-stat-chip__content">
        <span className="dgs-stat-chip__label">{label}</span>
        <span className="dgs-stat-chip__value">
          {safeMapped}/{safeTotal}
        </span>
        <span className="dgs-stat-chip__left">{left} left</span>
      </div>
    </div>
  );
};

const MappedStatChips = ({ dgsMapped, dgsTotal, galaxiesMapped, galaxiesTotal }) => (
  <div className="dgs-stat-chips" role="group" aria-label="Mapped stats">
    <MappedStatChip label="DGs" mapped={dgsMapped} total={dgsTotal} />
    <MappedStatChip label="Galaxies" mapped={galaxiesMapped} total={galaxiesTotal} />
  </div>
);

const TeamDGs = () => {
  const navigate = useNavigate();
  const { userData, loading } = useUserData();
  const { impersonateTeamId, isImpersonating } = useContext(SiteStateContext);
  const isAdmin = Boolean(userData?.adminUser);
  const isAdminImpersonating = isImpersonating && isAdmin;
  const effectiveTeamId =
  isAdminImpersonating ? impersonateTeamId : userData?.teamId;
  const canAccess = Boolean(userData?.verified) || isAdminImpersonating;
  const canEditIntel =
  isAdmin ||
  isAdminImpersonating ||
  (userData?.role === "leader" && Boolean(userData?.verified));
  const [search, setSearch] = useState("");
  const [selectedRouteGalaxies, setSelectedRouteGalaxies] = useState(() => new Set());
  const [routeModalOpen, setRouteModalOpen] = useState(false);
  const [routeGenerating, setRouteGenerating] = useState(false);
  const [routeProgress, setRouteProgress] = useState({ current: 0, total: 0, message: "" });
  const [routeResult, setRouteResult] = useState(null);
  const [routeError, setRouteError] = useState("");
  const [routeContext, setRouteContext] = useState(null);
  const [routeStartGalaxy, setRouteStartGalaxy] = useState("");
  const [routeGalaxyOptions, setRouteGalaxyOptions] = useState([]);
  const [dgLayers, setDgLayers] = useState(() => createEmptyLayers());
  const [dgGalaxyMeta, setDgGalaxyMeta] = useState(() => ({}));
  const [dgLoading, setDgLoading] = useState(true);
  const [dgRefreshing, setDgRefreshing] = useState(false);
  const [dgLastUpdated, setDgLastUpdated] = useState(null);
  const [dgError, setDgError] = useState("");
  const [dgFilterMode, setDgFilterMode] = useState(() => {
    if (typeof window === "undefined") return "all";
    const saved = localStorage.getItem("ss:dg-filter-mode");
    return saved === "mapped" || saved === "unmapped" || saved === "all" ? saved : "all";
  });
  const [dungeonIntelMap, setDungeonIntelMap] = useState(() => new Map());
  const [collapsedLayers, setCollapsedLayers] = useState(() => new Set());
  const [flaggedDungeons, setFlaggedDungeons] = useState(() => new Map());
  const [flagLoading, setFlagLoading] = useState(false);
  const [flagError, setFlagError] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editValues, setEditValues] = useState(() => createEmptyEditValues());
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const universeGraphRef = useRef(null);
  const universeGraphPromiseRef = useRef(null);
  const dungeonIntelRef = useRef(null);
  const dungeonIntelPromiseRef = useRef(null);
  const hasAuthUser = Boolean(getAuth().currentUser);

  useEffect(() => {
    if (!loading) {
      if (!userData) {
        navigate("/starsonata");
      } else if (!isAdminImpersonating && userData.teamId === "unassigned") {
        navigate("/join");
      } else if (!isAdminImpersonating && !userData.verified) {
        navigate("/pending");
      }
    }
  }, [userData, loading, navigate, isAdminImpersonating]);

  const buildDgLayers = useCallback((galaxies, intelMap) => {
    const layers = createEmptyLayers();
    const layerLookup = new Map(layers.map((layer) => [layer.layer, layer]));
    const dgIndex = new Map();
    const galaxyDfMap = {};
    const galaxyLayerMap = {};
    const galaxyNameMap = {};
    const dgIndexKeySet = new Set();

    galaxies.forEach((galaxy) => {
      const name = String(galaxy?.name ?? "").trim();
      if (!name) return;
      if (!name.startsWith("DG ")) {
        const dfValue = typeof galaxy?.df === "number" ? Math.round(galaxy.df * 100) : null;
        galaxyDfMap[name.toLowerCase()] = dfValue;
        galaxyLayerMap[name.toLowerCase()] = Number(galaxy?.layer);
        galaxyNameMap[name.toLowerCase()] = name;
        return;
      }
      const match = name.match(DG_NAME_PATTERN);
      if (!match) return;

      const baseGalaxy = match[1].trim();
      const levelNumber = Number(match[2]);
      const dgCode = match[3];
      const targetLayer = layerLookup.get(Number(galaxy?.layer));
      if (!targetLayer || Number.isNaN(levelNumber)) return;

      const key = `${targetLayer.id}:${baseGalaxy}:${dgCode}`;
      const existing = dgIndex.get(key);

      if (!existing || levelNumber > existing.levelNumber) {
        dgIndex.set(key, {
          galaxy: baseGalaxy,
          name: `${baseGalaxy} ${levelNumber}.${dgCode}`,
          levels: [],
          levelNumber,
          dgCode,
          layerId: targetLayer.id
        });
        dgIndexKeySet.add(`${targetLayer.id}:${baseGalaxy.toLowerCase()}:${dgCode}`);
      }
    });

    intelMap.forEach((levels, key) => {
      const splitIndex = key.lastIndexOf(":");
      if (splitIndex === -1) return;
      const galaxyLower = key.slice(0, splitIndex);
      const dgCode = key.slice(splitIndex + 1);
      const layerNumber = galaxyLayerMap[galaxyLower];
      const targetLayer = layerLookup.get(layerNumber);
      if (!targetLayer) return;
      const indexKey = `${targetLayer.id}:${galaxyLower}:${dgCode}`;
      if (dgIndexKeySet.has(indexKey)) return;

      const levelNumbers = (levels || []).map((level) => {
        const label = String(level?.label || "");
        const num = parseInt(label.split(".", 1)[0], 10);
        return Number.isFinite(num) ? num : null;
      }).filter((num) => num !== null);
      const maxLevel = levelNumbers.length ? Math.max(...levelNumbers) : 0;
      const galaxyName = galaxyNameMap[galaxyLower] || galaxyLower;

      dgIndex.set(indexKey, {
        galaxy: galaxyName,
        name: `${galaxyName} ${maxLevel}.${dgCode}`,
        levels: [],
        levelNumber: maxLevel,
        dgCode,
        layerId: targetLayer.id
      });
    });

    dgIndex.forEach((entry) => {
      const layer = layers.find((item) => item.id === entry.layerId);
      if (!layer) return;
      const intelKey = `${entry.galaxy.toLowerCase()}:${entry.dgCode}`;
      const intelLevels = intelMap.get(intelKey) || [];
      layer.dungeons.push({
        galaxy: entry.galaxy,
        name: entry.name,
        dgCode: entry.dgCode,
        levels: intelLevels
      });
    });

    layers.forEach((layer) => {
      layer.dungeons.sort((a, b) => {
        const galaxyCompare = a.galaxy.localeCompare(b.galaxy);
        if (galaxyCompare !== 0) return galaxyCompare;
        return a.name.localeCompare(b.name);
      });
    });

    return { layers, galaxyDfMap };
  }, []);

  const loadDungeonIntel = useCallback(
    async (teamId, options = {}) => {
      const { force = false, signal } = options;
      if (!teamId) return new Map();
      if (dungeonIntelPromiseRef.current) return dungeonIntelPromiseRef.current;
      if (!force && dungeonIntelRef.current) return dungeonIntelRef.current;

      dungeonIntelPromiseRef.current = (async () => {
        const map = await getDungeonIntelMap(teamId, { force, signal });
        dungeonIntelRef.current = map;
        dungeonIntelPromiseRef.current = null;
        return map;
      })();

      try {
        return await dungeonIntelPromiseRef.current;
      } catch (err) {
        dungeonIntelPromiseRef.current = null;
        throw err;
      }
    },
    []
  );

  const loadUniverseGraph = useCallback(async (signal, options = {}) => {
    const { force = false } = options;
    if (universeGraphPromiseRef.current) return universeGraphPromiseRef.current;
    if (!force && universeGraphRef.current) return universeGraphRef.current;

    if (!force) {
      const cached = readUniverseCache();
      if (cached) {
        const graph = buildUniverseGraph(cached.data);
        graph.lastUpdated = cached.timestamp;
        universeGraphRef.current = graph;
        return graph;
      }
    }

    universeGraphPromiseRef.current = (async () => {
      const res = await fetch(UNIVERSE_URL, { signal });
      if (!res.ok) {
        throw new Error(`Failed to load universe.json (${res.status})`);
      }

      const data = await res.json();
      const graph = buildUniverseGraph(data);
      const timestamp = Date.now();

      writeUniverseCache(data, timestamp);
      graph.lastUpdated = timestamp;
      universeGraphRef.current = graph;
      universeGraphPromiseRef.current = null;
      return graph;
    })();

    try {
      return await universeGraphPromiseRef.current;
    } catch (err) {
      universeGraphPromiseRef.current = null;
      throw err;
    }
  }, []);

  const refreshDungeons = useCallback(
    async ({ force = false, silent = false, signal } = {}) => {
      if (!canAccess) return;
      if (silent) {
        setDgRefreshing(true);
      } else {
        setDgLoading(true);
      }
      setDgError("");
      try {
        const [graph, intelMap] = await Promise.all([
        loadUniverseGraph(signal, { force }),
        loadDungeonIntel(effectiveTeamId, { force, signal })]
        );
        const { layers, galaxyDfMap } = buildDgLayers(graph.galaxies || [], intelMap);
        setDgLayers(layers);
        setDgGalaxyMeta(galaxyDfMap);
        setDungeonIntelMap(intelMap);
        setDgLastUpdated(graph.lastUpdated || Date.now());
      } catch (err) {
        if (err?.name !== "AbortError") {
          setDgError(err?.message || "Failed to load DG list.");
        }
      } finally {
        if (silent) {
          setDgRefreshing(false);
        } else {
          setDgLoading(false);
        }
      }
    },
    [buildDgLayers, loadUniverseGraph, loadDungeonIntel, effectiveTeamId, canAccess]
  );

  useEffect(() => {
    if (loading || !userData || !canAccess) return;

    let cancelled = false;
    const controller = new AbortController();

    const loadDungeons = async () => {
      await refreshDungeons({ signal: controller.signal });
    };

    loadDungeons();
    const interval = setInterval(() => {
      if (!cancelled) {
        refreshDungeons({ force: true, silent: true });
      }
    }, UNIVERSE_REFRESH_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
      controller.abort();
    };
  }, [loading, userData, refreshDungeons, canAccess]);

  useEffect(() => {
    if (!effectiveTeamId) return;
    const stored = localStorage.getItem(`${DG_COLLAPSE_STORAGE_KEY}:${effectiveTeamId}`);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setCollapsedLayers(new Set(parsed));
          return;
        }
      } catch (err) {}
    }
    setCollapsedLayers(new Set(LAYER_DEFS.map((layer) => layer.id)));
  }, [effectiveTeamId]);

  useEffect(() => {
    if (!effectiveTeamId) return;
    localStorage.setItem(
      `${DG_COLLAPSE_STORAGE_KEY}:${effectiveTeamId}`,
      JSON.stringify(Array.from(collapsedLayers))
    );
  }, [collapsedLayers, effectiveTeamId]);

  useEffect(() => {
    if (!canAccess || !effectiveTeamId) return;
    setFlagLoading(true);
    setFlagError("");

    const ref = collection(db, "teams", effectiveTeamId, "dgFlags");
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        const next = new Map();
        snap.forEach((docSnap) => {
          const data = docSnap.data();
          const key = data?.key || decodeURIComponent(docSnap.id);
          if (key) {
            next.set(key, {
              name: data?.flaggedByName || "Unknown",
              uid: data?.flaggedBy || null
            });
          }
        });
        setFlaggedDungeons(next);
        setFlagLoading(false);
      },
      (err) => {
        setFlagError(err?.message || "Failed to load flags.");
        setFlagLoading(false);
      }
    );

    return () => unsubscribe();
  }, [canAccess, effectiveTeamId]);

  const toggleFlagDungeon = useCallback(
    async (dungeon) => {
      if (!effectiveTeamId) return;
      const galaxy = dungeon?.galaxy;
      const dgCode = dungeon?.dgCode;
      if (!galaxy || !dgCode) return;

      const key = buildFlagKey(galaxy, dgCode);
      const docId = buildFlagDocId(galaxy, dgCode);
      const flagRef = doc(db, "teams", effectiveTeamId, "dgFlags", docId);
      const isFlagged = flaggedDungeons.has(key);

      setFlaggedDungeons((prev) => {
        const next = new Map(prev);
        if (isFlagged) {
          next.delete(key);
        } else {
          next.set(key, {
            name: userData?.username || "Unknown",
            uid: userData?.uid || null
          });
        }
        return next;
      });

      try {
        if (isFlagged) {
          await deleteDoc(flagRef);
        } else {
          await setDoc(flagRef, {
            key,
            galaxy,
            dgCode,
            flaggedBy: userData?.uid || null,
            flaggedTeamId: userData?.teamId || null,
            flaggedByName: userData?.username || null,
            updatedAt: serverTimestamp()
          });
        }
      } catch (err) {
        setFlaggedDungeons((prev) => {
          const next = new Map(prev);
          if (isFlagged) {
            next.set(key, {
              name: userData?.username || "Unknown",
              uid: userData?.uid || null
            });
          } else {
            next.delete(key);
          }
          return next;
        });
        setFlagError(err?.message || "Failed to update flag.");
      }
    },
    [effectiveTeamId, flaggedDungeons]
  );

  const openEditModal = useCallback(
    (target) => {
      if (!canEditIntel || !effectiveTeamId) return;
      const safeTarget = target || null;
      setEditTarget(safeTarget);
      setEditValues({
        monsterType: String(safeTarget?.values?.monsterType || ""),
        boss: String(safeTarget?.values?.boss || ""),
        guards: String(safeTarget?.values?.guards || "")
      });
      setEditError("");
      setEditModalOpen(true);
    },
    [canEditIntel, effectiveTeamId]
  );

  const closeEditModal = useCallback(() => {
    if (editSaving) return;
    setEditModalOpen(false);
    setEditTarget(null);
    setEditValues(createEmptyEditValues());
    setEditError("");
  }, [editSaving]);

  const getEditFieldLabel = useCallback((field) => {
    if (field === "boss") return "Boss Name";
    if (field === "monsterType") return "Mob Name";
    if (field === "guards") return "Guards Name";
    return "Value";
  }, []);

  const editNameSuggestionsByField = useMemo(() => {
    const byField = {
      boss: new Set(),
      monsterType: new Set(),
      guards: new Set()
    };

    const normalizeSuggestionPart = (value) =>
    String(value || "").
    replace(/^boss\s*:\s*/i, "").
    replace(/^guards?\s*:\s*/i, "").
    trim();

    const addName = (field, value) => {
      if (!byField[field]) return;
      const raw = String(value || "").trim();
      if (!raw) return;

      if (field === "guards" || field === "monsterType") {
        const parts = raw.split(",").map((part) => normalizeSuggestionPart(part)).filter(Boolean);
        if (parts.length > 1) {
          parts.forEach((part) => byField[field].add(part));
          return;
        }
      }

      const name = normalizeSuggestionPart(raw);
      if (!name) return;
      byField[field].add(name);
    };

    (Array.isArray(dungeonSeedData) ? dungeonSeedData : []).forEach((entry) => {
      const levels = Array.isArray(entry?.dungeonLevels) ? entry.dungeonLevels : [];
      levels.forEach((level) => {
        const monster = String(level?.dungeonMonster || "").trim();
        if (!monster) return;
        addName("monsterType", monster);
        addName("guards", monster);
      });

      const bosses = entry?.dungeonBosses || {};
      Object.values(bosses).forEach((boss) => addName("boss", boss));
    });

    if (dungeonIntelMap instanceof Map) {
      dungeonIntelMap.forEach((levels) => {
        (Array.isArray(levels) ? levels : []).forEach((level) => {
          addName("monsterType", level?.monsterType);
          addName("boss", level?.boss);
          (Array.isArray(level?.guards) ? level.guards : []).forEach((guard) => addName("guards", guard));
        });
      });
    }

    return {
      boss: Array.from(byField.boss).sort((a, b) => a.localeCompare(b)),
      monsterType: Array.from(byField.monsterType).sort((a, b) => a.localeCompare(b)),
      guards: Array.from(byField.guards).sort((a, b) => a.localeCompare(b))
    };
  }, [dungeonIntelMap]);

  const getEditFieldsForTarget = useCallback((target) => {
    if (target?.isBossLevel) return ["boss", "guards"];
    return ["monsterType"];
  }, []);

  const getEditSuggestionsForField = useCallback((field, value) => {
    if (!field || !editNameSuggestionsByField[field]) return [];
    const pool = editNameSuggestionsByField[field];
    const query = String(value || "").trim().toLowerCase();
    if (!query) return pool.slice(0, 10);
    return pool
      .filter((name) => {
        const lower = name.toLowerCase();
        return lower.includes(query) && lower !== query;
      })
      .slice(0, 10);
  }, [editNameSuggestionsByField]);

  const editFields = useMemo(
    () => (editTarget ? getEditFieldsForTarget(editTarget) : []),
    [editTarget, getEditFieldsForTarget]
  );

  const editSuggestions = useMemo(() => {
    const next = {};
    editFields.forEach((field) => {
      next[field] = getEditSuggestionsForField(field, editValues[field] || "");
    });
    return next;
  }, [editFields, getEditSuggestionsForField, editValues]);

  const handleSaveEdit = useCallback(async () => {
    if (!editTarget || !effectiveTeamId || !canEditIntel || !editFields.length) return;

    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setEditError("You are not signed in. Please sign in again and retry.");
      alert("You are not signed in. Please sign in again and retry.");
      return;
    }

    try {
      await currentUser.getIdToken(true);
    } catch (tokenErr) {
      setEditError("Could not refresh your login token. Please sign in again.");
      alert("Could not refresh your login token. Please sign in again.");
      return;
    }

    setEditSaving(true);
    setEditError("");
    let updatedAny = false;
    try {
      const idToken = await currentUser.getIdToken();

      for (const field of editFields) {
        const response = await fetch(DG_EDIT_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({
            idToken,
            teamId: effectiveTeamId,
            galaxy: editTarget.galaxy,
            dgId: editTarget.dgCode,
            levelLabel: editTarget.levelLabel,
            field,
            newValue: editValues[field] || ""
          })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload?.ok) {
          const errMessage = payload?.error?.message || "Failed to update this DG intel value.";
          throw new Error(`Failed to update ${getEditFieldLabel(field).toLowerCase()}: ${errMessage}`);
        }
        updatedAny = true;
      }

      await refreshDungeons({ force: true });
      setEditModalOpen(false);
      setEditTarget(null);
      setEditValues(createEmptyEditValues());
    } catch (err) {
      if (updatedAny) {
        try {
          await refreshDungeons({ force: true });
        } catch (refreshErr) {

        }
      }
      setEditError(err?.message || "Failed to update this DG intel value.");
    } finally {
      setEditSaving(false);
    }
  }, [
    editTarget,
    effectiveTeamId,
    canEditIntel,
    editFields,
    editValues,
    getEditFieldLabel,
    refreshDungeons
  ]);


  const groupDungeonsByGalaxy = (dungeons) =>
  dungeons.reduce((acc, dungeon) => {
    const key = dungeon.galaxy || "Unknown";
    if (!acc[key]) acc[key] = [];
    acc[key].push(dungeon);
    return acc;
  }, {});

  const splitDungeonLevels = (levels) => {
    const trunk = [];
    const branches = { A: [], B: [], C: [] };

    levels.forEach((level) => {
      const match = level.label.match(/^(.*?)([A-Z])$/);
      const suffix = match ? match[2] : null;
      if (suffix === "A" || suffix === "B" || suffix === "C") {
        branches[suffix].push(level);
      } else {
        trunk.push(level);
      }
    });

    return {
      trunk,
      branches,
      hasSplit: branches.A.length > 0 || branches.B.length > 0 || branches.C.length > 0
    };
  };

  const normalizedSearch = search.trim().toLowerCase();

  const dungeonContainsSearch = useCallback((dungeon) => {
    if (!normalizedSearch) return true;
    const nameMatch = `${dungeon.name || ""} ${dungeon.galaxy || ""}`.toLowerCase();
    if (nameMatch.includes(normalizedSearch)) return true;
    return (dungeon.levels || []).some((level) => {
      const adds = (level.monsterType || "").toLowerCase();
      const boss = (level.boss || "").toLowerCase();
      const guards = (level.guards || []).join(", ").toLowerCase();
      return adds.includes(normalizedSearch) || boss.includes(normalizedSearch) || guards.includes(normalizedSearch);
    });
  }, [normalizedSearch]);

  const isDungeonVisible = useCallback(
    (dungeon) => {
      const hasLevels = Array.isArray(dungeon.levels) && dungeon.levels.length > 0;
      if (dgFilterMode === "mapped" && !hasLevels) return false;
      if (dgFilterMode === "unmapped" && hasLevels) return false;
      return dungeonContainsSearch(dungeon);
    },
    [dgFilterMode, dungeonContainsSearch]
  );

  useEffect(() => {
    localStorage.setItem("ss:dg-filter-mode", dgFilterMode);
  }, [dgFilterMode]);

  const hasActiveFilters = normalizedSearch.length > 0 || dgFilterMode !== "all";

  const toggleLayerCollapse = useCallback((layerId) => {
    setCollapsedLayers((prev) => {
      const next = new Set(prev);
      if (next.has(layerId)) {
        next.delete(layerId);
      } else {
        next.add(layerId);
      }
      return next;
    });
  }, []);

  const layers = useMemo(() => dgLayers, [dgLayers]);

  const visibleGalaxyNames = useMemo(() => {
    const names = new Set();
    layers.forEach((layer) => {
      if (collapsedLayers.has(layer.id)) return;
      layer.dungeons.filter(isDungeonVisible).forEach((dungeon) => {
        if (dungeon.galaxy) names.add(dungeon.galaxy);
      });
    });
    return Array.from(names);
  }, [layers, collapsedLayers, isDungeonVisible]);

  const allVisibleSelected =
  visibleGalaxyNames.length > 0 && visibleGalaxyNames.every((name) => selectedRouteGalaxies.has(name));

  const generateDisabled = routeGenerating || selectedRouteGalaxies.size < 2;
  const clearDisabled = routeGenerating || selectedRouteGalaxies.size === 0 && !hasActiveFilters;

  const toggleRouteGalaxy = (galaxyName) => {
    setSelectedRouteGalaxies((prev) => {
      const next = new Set(prev);
      if (next.has(galaxyName)) {
        next.delete(galaxyName);
      } else {
        next.add(galaxyName);
      }
      return next;
    });
  };

  const handleClearToolbar = useCallback(() => {
    setSelectedRouteGalaxies(new Set());
    setSearch("");
    setDgFilterMode("all");
  }, []);


  const bfsHopDistances = (startId, adjacency, targetIds) => {
    const distances = new Map([[startId, 0]]);
    const queue = [startId];
    let head = 0;

    const remaining = new Set(targetIds);
    remaining.delete(startId);

    while (head < queue.length && remaining.size > 0) {
      const current = queue[head++];
      const currentDist = distances.get(current) ?? 0;

      const neighbors = adjacency[current] || [];
      for (const neighbor of neighbors) {
        if (distances.has(neighbor)) continue;
        const nextDist = currentDist + 1;
        distances.set(neighbor, nextDist);
        if (remaining.has(neighbor)) remaining.delete(neighbor);
        queue.push(neighbor);
      }
    }

    const result = new Map();
    for (const targetId of targetIds) {
      const dist = distances.get(targetId);
      if (dist !== undefined) result.set(targetId, dist);
    }
    return result;
  };

  const computeHopMatrix = async (ids, graph, onProgress) => {
    const n = ids.length;
    const targetIds = new Set(ids);
    const matrix = Array.from({ length: n }, () => Array(n).fill(Infinity));

    for (let i = 0; i < n; i++) {
      matrix[i][i] = 0;
    }

    for (let i = 0; i < n; i++) {
      onProgress?.(i, n, graph.idToName[ids[i]] || ids[i]);
      await new Promise((r) => setTimeout(r, 0));

      const distToTargets = bfsHopDistances(ids[i], graph.adjacency, targetIds);
      for (let j = 0; j < n; j++) {
        if (i === j) continue;
        const dist = distToTargets.get(ids[j]);
        if (dist === undefined) {
          throw new Error(
            `No route found between ${graph.idToName[ids[i]] || ids[i]} and ${graph.idToName[ids[j]] || ids[j]}.`
          );
        }
        matrix[i][j] = dist;
      }
    }

    return matrix;
  };

  const solveHeldKarpPath = (distMatrix) => {
    const n = distMatrix.length;
    const size = 1 << n;
    const dp = Array.from({ length: size }, () => Array(n).fill(Infinity));
    const parent = Array.from({ length: size }, () => Array(n).fill(-1));

    for (let j = 0; j < n; j++) {
      dp[1 << j][j] = 0;
    }

    for (let mask = 1; mask < size; mask++) {
      for (let j = 0; j < n; j++) {
        if ((mask & 1 << j) === 0) continue;
        const prevMask = mask ^ 1 << j;
        if (prevMask === 0) continue;

        for (let i = 0; i < n; i++) {
          if ((prevMask & 1 << i) === 0) continue;
          const hop = distMatrix[i][j];
          if (!Number.isFinite(hop)) continue;
          const cost = dp[prevMask][i] + hop;
          if (cost < dp[mask][j]) {
            dp[mask][j] = cost;
            parent[mask][j] = i;
          }
        }
      }
    }

    const fullMask = size - 1;
    let bestEnd = -1;
    let bestCost = Infinity;
    for (let j = 0; j < n; j++) {
      if (dp[fullMask][j] < bestCost) {
        bestCost = dp[fullMask][j];
        bestEnd = j;
      }
    }

    if (bestEnd === -1 || !Number.isFinite(bestCost)) {
      throw new Error("Unable to compute a route for the selected galaxies.");
    }

    const order = [];
    let mask = fullMask;
    let current = bestEnd;
    while (current !== -1) {
      order.push(current);
      const prev = parent[mask][current];
      mask ^= 1 << current;
      current = prev;
    }

    order.reverse();
    return { order, cost: bestCost };
  };

  const solveNearestNeighbor = (distMatrix) => {
    const n = distMatrix.length;

    const buildFromStart = (start) => {
      const visited = new Set([start]);
      const order = [start];
      let cost = 0;
      let current = start;

      while (order.length < n) {
        let bestNext = -1;
        let bestDist = Infinity;

        for (let j = 0; j < n; j++) {
          if (visited.has(j)) continue;
          const d = distMatrix[current][j];
          if (d < bestDist) {
            bestDist = d;
            bestNext = j;
          }
        }

        if (bestNext === -1 || !Number.isFinite(bestDist)) return null;
        visited.add(bestNext);
        order.push(bestNext);
        cost += bestDist;
        current = bestNext;
      }

      return { order, cost };
    };

    let best = null;
    for (let start = 0; start < n; start++) {
      const candidate = buildFromStart(start);
      if (!candidate) continue;
      if (!best || candidate.cost < best.cost) best = candidate;
    }

    if (!best) throw new Error("Unable to compute a route for the selected galaxies.");
    return best;
  };

  const solveRouteOrder = (distMatrix) => {
    const n = distMatrix.length;
    if (n <= 12) return solveHeldKarpPath(distMatrix);
    return solveNearestNeighbor(distMatrix);
  };

  const solveRouteOrderWithStart = (distMatrix, startIdx) => {
    const n = distMatrix.length;
    if (n <= 12) {
      const size = 1 << n;
      const dp = Array.from({ length: size }, () => Array(n).fill(Infinity));
      const parent = Array.from({ length: size }, () => Array(n).fill(-1));
      const startMask = 1 << startIdx;
      dp[startMask][startIdx] = 0;

      for (let mask = 1; mask < size; mask++) {
        if ((mask & startMask) === 0) continue;
        for (let j = 0; j < n; j++) {
          if ((mask & 1 << j) === 0) continue;
          const prevMask = mask ^ 1 << j;
          if (prevMask === 0) continue;
          for (let i = 0; i < n; i++) {
            if ((prevMask & 1 << i) === 0) continue;
            const hop = distMatrix[i][j];
            if (!Number.isFinite(hop)) continue;
            const cost = dp[prevMask][i] + hop;
            if (cost < dp[mask][j]) {
              dp[mask][j] = cost;
              parent[mask][j] = i;
            }
          }
        }
      }

      const fullMask = size - 1;
      let bestEnd = -1;
      let bestCost = Infinity;
      for (let j = 0; j < n; j++) {
        if (dp[fullMask][j] < bestCost) {
          bestCost = dp[fullMask][j];
          bestEnd = j;
        }
      }
      if (bestEnd === -1 || !Number.isFinite(bestCost)) {
        throw new Error("Unable to compute a route for the selected galaxies.");
      }

      const order = [];
      let mask = fullMask;
      let current = bestEnd;
      while (current !== -1) {
        order.push(current);
        const prev = parent[mask][current];
        mask ^= 1 << current;
        current = prev;
      }
      order.reverse();
      return { order, cost: bestCost };
    }

    const visited = new Set([startIdx]);
    const order = [startIdx];
    let cost = 0;
    let current = startIdx;
    while (order.length < n) {
      let bestNext = -1;
      let bestDist = Infinity;
      for (let j = 0; j < n; j++) {
        if (visited.has(j)) continue;
        const d = distMatrix[current][j];
        if (d < bestDist) {
          bestDist = d;
          bestNext = j;
        }
      }
      if (bestNext === -1 || !Number.isFinite(bestDist)) {
        throw new Error("Unable to compute a route for the selected galaxies.");
      }
      visited.add(bestNext);
      order.push(bestNext);
      cost += bestDist;
      current = bestNext;
    }
    return { order, cost };
  };

  const handleGenerateRoute = async () => {
    const selectedNames = Array.from(selectedRouteGalaxies);
    if (selectedNames.length < 2) return;

    setRouteError("");
    setRouteResult(null);
    setRouteModalOpen(true);
    setRouteGenerating(true);

    const totalSteps = selectedNames.length + 2;
    try {
      setRouteProgress({ current: 0, total: totalSteps, message: "Loading universe data..." });
      const graph = await loadUniverseGraph();

      const ids = selectedNames.map((name) => {
        const id = graph.nameToId[name.trim().toLowerCase()];
        if (!id) throw new Error(`Galaxy not found in universe.json: ${name}`);
        return id;
      });
      const allNames = (graph.galaxies || []).
      map((galaxy) => String(galaxy?.name || "").trim()).
      filter(Boolean).
      sort((a, b) => a.localeCompare(b));
      setRouteGalaxyOptions(allNames);

      setRouteProgress({ current: 1, total: totalSteps, message: "Calculating shortest jumps..." });
      const distMatrix = await computeHopMatrix(ids, graph, (idx, total, fromName) => {
        setRouteProgress({
          current: 1 + idx,
          total: totalSteps,
          message: `Calculating from ${fromName} (${idx + 1}/${total})...`
        });
      });

      setRouteProgress({ current: totalSteps - 1, total: totalSteps, message: "Optimizing order..." });
      await new Promise((r) => setTimeout(r, 0));

      const { order, cost } = solveRouteOrder(distMatrix);
      const orderedNames = order.map((idx) => graph.idToName[ids[idx]] || selectedNames[idx]);

      setRouteResult({ order: orderedNames, totalJumps: cost });
      setRouteContext({ graph, ids, distMatrix, selectedNames });
      setRouteStartGalaxy(orderedNames[0] || "");
      setRouteProgress({ current: totalSteps, total: totalSteps, message: "Done." });
    } catch (err) {
      setRouteError(err?.message || String(err));
      setRouteProgress({ current: totalSteps, total: totalSteps, message: "Error." });
    } finally {
      setRouteGenerating(false);
    }
  };

  const handleRecalculateFromStart = async () => {
    if (!routeContext || !routeStartGalaxy) return;
    const { graph, selectedNames, ids: cachedIds, distMatrix: cachedMatrix } = routeContext;

    setRouteError("");
    setRouteGenerating(true);
    try {
      const startName = routeStartGalaxy.trim();
      const startId = graph.nameToId[startName.toLowerCase()];
      if (!startId) {
        throw new Error(`Galaxy not found in universe.json: ${startName}`);
      }

      let ids = cachedIds;
      let distMatrix = cachedMatrix;
      let startIdx = ids.findIndex((id) => id === startId);

      if (startIdx === -1) {
        const nextNames = [startName, ...selectedNames].filter(
          (value, index, self) => self.indexOf(value) === index
        );
        const totalSteps = nextNames.length + 2;
        setRouteProgress({ current: 0, total: totalSteps, message: "Recalculating from start..." });

        ids = nextNames.map((name) => {
          const id = graph.nameToId[name.trim().toLowerCase()];
          if (!id) throw new Error(`Galaxy not found in universe.json: ${name}`);
          return id;
        });

        distMatrix = await computeHopMatrix(ids, graph, (idx, total, fromName) => {
          setRouteProgress({
            current: 1 + idx,
            total: totalSteps,
            message: `Calculating from ${fromName} (${idx + 1}/${total})...`
          });
        });

        startIdx = 0;
      }

      const { order, cost } = solveRouteOrderWithStart(distMatrix, startIdx);
      const orderedNames = order.map((idx) => graph.idToName[ids[idx]] || selectedNames[idx]);
      setRouteResult({ order: orderedNames, totalJumps: cost });
      setRouteContext({ graph, ids, distMatrix, selectedNames });
      setRouteProgress({ current: 1, total: 1, message: "Done." });
    } catch (err) {
      setRouteError(err?.message || String(err));
    } finally {
      setRouteGenerating(false);
    }
  };

  const levelToToken = (level, dungeon) => {
    if (!level) return null;
    const levelLabel = String(level.label || "").trim();
    const isBossLevel = isBossLevelLabel(levelLabel);
    const boss = String(level.boss || "").trim();
    const guardsList = (Array.isArray(level.guards) ? level.guards : []).
    map((entry) => String(entry || "").trim()).
    filter(Boolean);
    const guards = guardsList.length ?
    guardsList.join(", ") :
    isBossLevel ?
    String(level.monsterType || "").trim() :
    "";
    return {
      galaxy: dungeon?.galaxy || "",
      dgCode: dungeon?.dgCode || "",
      label: levelLabel,
      levelLabel,
      isBossLevel,
      adds: isBossLevel ? "" : String(level.monsterType || "").trim(),
      boss,
      guards,
      dungeonName: dungeon?.name || ""
    };
  };

  const tokenMatchesSearch = (value) => {
    if (!normalizedSearch) return false;
    return (value || "").toLowerCase().includes(normalizedSearch);
  };

  const buildDungeonRouteLines = (levels, dungeon) => {
    const { trunk, branches, hasSplit } = splitDungeonLevels(levels);

    if (!hasSplit) {
      return [
      {
        indent: 0,
        tag: "Main",
        tokens: trunk.map((level) => levelToToken(level, dungeon)).filter(Boolean)
      }];

    }

    const splitNode = trunk[trunk.length - 1];
    const preSplit = trunk.slice(0, -1);

    const lines = [
    {
      indent: 0,
      tag: "Main",
      tokens: [...preSplit, splitNode].
      filter(Boolean).
      map((level) => levelToToken(level, dungeon)).
      filter(Boolean)
    }];


    const aTokens = branches.A.map((level) => levelToToken(level, dungeon)).filter(Boolean);
    if (aTokens.length) lines.push({ indent: 0, tag: "A", tokens: aTokens });

    if (branches.C.length > 0 && branches.B.length > 0) {
      const bHead = branches.B[0];
      const bTail = branches.B.slice(1);
      lines.push({
        indent: 0,
        tag: "B",
        tokens: [levelToToken(bHead, dungeon)].filter(Boolean)
      });
      const bTailTokens = bTail.map((level) => levelToToken(level, dungeon)).filter(Boolean);
      if (bTailTokens.length) lines.push({ indent: 1, tag: "B", tokens: bTailTokens });
      const cTokens = branches.C.map((level) => levelToToken(level, dungeon)).filter(Boolean);
      if (cTokens.length) lines.push({ indent: 1, tag: "C", tokens: cTokens });
    } else {
      const bTokens = branches.B.map((level) => levelToToken(level, dungeon)).filter(Boolean);
      if (bTokens.length) lines.push({ indent: 0, tag: "B", tokens: bTokens });
      const cTokens = branches.C.map((level) => levelToToken(level, dungeon)).filter(Boolean);
      if (cTokens.length) lines.push({ indent: 0, tag: "C", tokens: cTokens });
    }

    return lines;
  };

  if (loading || !userData) return <p>Loading...</p>;

  const routePercent = routeProgress.total ?
  Math.min(100, Math.round(routeProgress.current / routeProgress.total * 100)) :
  0;
  const refreshMinutes = Math.round(UNIVERSE_REFRESH_MS / 60000);

  return (
    <div style={{ padding: "0 1rem" }}>
      <div
        style={{
          border: "1px solid #444",
          borderRadius: "10px",
          padding: "1.5rem",
          backgroundColor: "#1e1e1e",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          width: "100%",
          maxWidth: "1320px",
          margin: "0 auto",
          boxSizing: "border-box"
        }}>
        
        <h3 style={{ textAlign: "center", margin: "0 0 0.75rem" }}>DGs</h3>
        {canEditIntel &&
        <div style={{ textAlign: "center", opacity: 0.8, marginBottom: "0.75rem", fontSize: "0.85rem" }}>
            Leader tools: click level names in route lines to edit.
          </div>
        }
        <div style={{ textAlign: "center", opacity: 0.85, marginBottom: "1rem" }}>
          
        </div>
        <div className="dgs-toolbar">
          <div className="dgs-toolbar__row dgs-toolbar__row--filters">
            <div className="dgs-toolbar__search">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search DG or AI (e.g. Sol, Icepicks)"
                className="dgs-toolbar__search-input" />

            </div>
          </div>
          <div className="dgs-toolbar__row dgs-toolbar__row--filters">
            <div className="dgs-toolbar__segment" role="group" aria-label="Show DGs">
              <button
                type="button"
                className={`dgs-toolbar__segment-button${dgFilterMode === "all" ? " is-active" : ""}`}
                aria-pressed={dgFilterMode === "all"}
                onClick={() => setDgFilterMode("all")}>

                All
              </button>
              <button
                type="button"
                className={`dgs-toolbar__segment-button${dgFilterMode === "mapped" ? " is-active" : ""}`}
                aria-pressed={dgFilterMode === "mapped"}
                onClick={() => setDgFilterMode("mapped")}>

                Mapped
              </button>
              <button
                type="button"
                className={`dgs-toolbar__segment-button${dgFilterMode === "unmapped" ? " is-active" : ""}`}
                aria-pressed={dgFilterMode === "unmapped"}
                onClick={() => setDgFilterMode("unmapped")}>

                Unmapped
              </button>
            </div>
          </div>
          {(dgLoading || dgRefreshing || dgError) &&
          <div className="dgs-toolbar__status">
              {dgError ?
            dgError :
            dgRefreshing ?
            "Refreshing DG list from local universe data..." :
            "Loading DG list from local universe data..."}
            </div>
          }
          <div className="dgs-toolbar__row dgs-toolbar__row--select">
            <div className="dgs-toolbar__cluster">
              <label className="dgs-toolbar__checkbox">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={() => {
                    setSelectedRouteGalaxies((prev) => {
                      const next = new Set(prev);
                      if (allVisibleSelected) {
                        visibleGalaxyNames.forEach((name) => next.delete(name));
                      } else {
                        visibleGalaxyNames.forEach((name) => next.add(name));
                      }
                      return next;
                    });
                  }}
                  disabled={visibleGalaxyNames.length === 0} />

                Select visible
              </label>
              <span className="dgs-toolbar__chip">Selected: {selectedRouteGalaxies.size}</span>
            </div>
          </div>
          <div className="dgs-toolbar__row dgs-toolbar__row--actions">
            <div className="dgs-toolbar__cluster">
              <button
                type="button"
                className="back-button dgs-toolbar__button dgs-toolbar__button--secondary"
                onClick={() => refreshDungeons({ force: true })}
                disabled={dgLoading || dgRefreshing}
                title="Refresh DG list">

                Refresh
              </button>
              <button
                type="button"
                className="back-button dgs-toolbar__button dgs-toolbar__button--primary"
                onClick={handleGenerateRoute}
                disabled={generateDisabled}
                title={selectedRouteGalaxies.size < 2 ? "Select at least 2 galaxies" : "Generate route"}>

                Generate Route
              </button>
              <button
                type="button"
                className="back-button dgs-toolbar__button dgs-toolbar__button--secondary"
                onClick={handleClearToolbar}
                disabled={clearDisabled}
                title={clearDisabled ? "Nothing to clear" : "Clear selections and filters"}>

                Clear
              </button>
            </div>
          </div>
        </div>
        {flagError &&
        <div style={{ textAlign: "center", color: "#ff6b6b", marginBottom: "0.75rem" }}>
            {flagError}
          </div>
        }
        <div className="dgs-toolbar__updated">
          Last updated: {dgLastUpdated ? new Date(dgLastUpdated).toLocaleString() : "n/a"}
        </div>
        {selectedRouteGalaxies.size > 0 &&
        <div
          style={{
            textAlign: "center",
            opacity: 0.8,
            marginBottom: "1rem",
            fontSize: "0.85rem"
          }}>

            {Array.from(selectedRouteGalaxies).join(" \u2022 ")}
          </div>
        }

        <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
          {layers.map((layer) => {
            const filteredDungeons = layer.dungeons.filter(isDungeonVisible);
            const groupedDungeons = groupDungeonsByGalaxy(filteredDungeons);
            const isCollapsed = collapsedLayers.has(layer.id);
            const totalDgs = layer.dungeons.length;
            const mappedDgs = layer.dungeons.filter(
              (dungeon) => Array.isArray(dungeon.levels) && dungeon.levels.length > 0
            ).length;
            const unmappedDgs = totalDgs - mappedDgs;
            const dungeonByGalaxy = layer.dungeons.reduce((acc, dungeon) => {
              const key = dungeon.galaxy || "Unknown";
              if (!acc[key]) acc[key] = [];
              acc[key].push(dungeon);
              return acc;
            }, {});
            const totalGalaxies = Object.keys(dungeonByGalaxy).length;
            const mappedGalaxies = Object.values(dungeonByGalaxy).filter((dungeons) =>
              dungeons.every((dungeon) => Array.isArray(dungeon.levels) && dungeon.levels.length > 0)
            ).length;
            const unmappedGalaxies = Object.values(dungeonByGalaxy).filter((dungeons) =>
              dungeons.every((dungeon) => !Array.isArray(dungeon.levels) || dungeon.levels.length === 0)
            ).length;
            return (
              <div
                key={layer.id}
                style={{
                  border: "1px solid #2b2b2b",
                  borderRadius: "10px",
                  padding: "1rem",
                  background: "#171717"
                }}>
                
              <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto 1fr",
                    alignItems: "center",
                    gap: "1rem",
                    marginBottom: "0.25rem"
                  }}>
                  
                <span />
                <div style={{ fontSize: "1.05rem", fontWeight: 700, textAlign: "center" }}>
                  {layer.title}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                      type="button"
                      className="back-button"
                      onClick={() => toggleLayerCollapse(layer.id)}>
                      
                    {isCollapsed ? "Show" : "Hide"}
                  </button>
                </div>
              </div>
              <MappedStatChips
                dgsMapped={mappedDgs}
                dgsTotal={totalDgs}
                galaxiesMapped={mappedGalaxies}
                galaxiesTotal={totalGalaxies}
              />

              {!isCollapsed &&
                <>
                  {filteredDungeons.length === 0 ?
                  <div style={{ opacity: 0.75 }}>
                      {dgLoading ?
                    "Loading DGs..." :
                    normalizedSearch ?
                    "No matches." :
                    dgFilterMode === "unmapped" ?
                    "No unmapped dungeons." :
                    dgFilterMode === "mapped" ?
                    "No mapped dungeons." :
                    "No dungeons."}
                    </div> :

                  <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                      {Object.entries(groupedDungeons).map(([galaxy, dungeons]) => {
                      const dfValue = dgGalaxyMeta[galaxy.toLowerCase()];
                      const galaxyBosses = Array.from(
                        new Set(
                          dungeons.flatMap((dungeon) =>
                          (dungeon.levels || []).map((level) => level.boss).filter(Boolean)
                          )
                        )
                      ).sort((a, b) => a.localeCompare(b));
                      return (
                        <details
                          key={`${layer.id}:${galaxy}`}
                          style={{
                            border: "1px solid #2b2b2b",
                            borderRadius: "10px",
                            padding: "0.75rem",
                            background: "#191919"
                          }}>
                          
                            <summary
                            style={{
                              cursor: "pointer",
                              listStyle: "none"
                            }}>
                            
                              <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "1rem"
                              }}>
                              
                                <div style={{ fontWeight: 700 }}>
                                  {galaxy}
                                  {dfValue !== null && dfValue !== undefined ?
                                <span style={{ marginLeft: "0.5rem", opacity: 0.8 }}>DF {dfValue}</span> :
                                null}
                                  {galaxyBosses.length > 0 &&
                                <span className="dgs-galaxy-bosses">
                                      {galaxyBosses.map((boss) =>
                                  <span key={`${layer.id}:${galaxy}:boss:${boss}`} className="dgs-boss-badge dgs-boss-badge--small">
                                          {boss}
                                        </span>
                                  )}
                                    </span>
                                }
                                </div>
                                <input
                                type="checkbox"
                                checked={selectedRouteGalaxies.has(galaxy)}
                                onChange={() => toggleRouteGalaxy(galaxy)}
                                onClick={(e) => e.stopPropagation()}
                                title="Include in route" />
                              
                              </div>
                            </summary>
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", marginTop: "0.6rem" }}>
                              {dungeons.map((dungeon) => {
                              const hasLevels = Array.isArray(dungeon.levels) && dungeon.levels.length > 0;
                              const flagKey = buildFlagKey(dungeon.galaxy, dungeon.dgCode);
                              const flaggedMeta = flaggedDungeons.get(flagKey);
                              const isFlagged = Boolean(flaggedMeta);
                              return (
                                <details
                                  key={`${layer.id}:${dungeon.galaxy}:${dungeon.name}`}
                                  style={{
                                    border: isFlagged ? "1px solid #b00020" : "1px solid #2b2b2b",
                                    borderRadius: "10px",
                                    padding: "0.5rem 0.75rem",
                                    background: "#1e1e1e"
                                  }}>
                                  
                                    <summary style={{ cursor: "pointer", listStyle: "none" }}>
                                      <span
                                      style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: "0.5rem 0.75rem",
                                        alignItems: "center"
                                      }}>
                                      
                                        <span style={{ fontWeight: 700 }}>{dungeon.name}</span>
                                        {hasLevels ?
                                      <>
                                            {Array.from(
                                          new Set(
                                            dungeon.levels.
                                            filter((level) => !level.boss).
                                            map((level) => level.monsterType).
                                            filter(Boolean)
                                          )
                                        ).
                                        sort((a, b) => a.localeCompare(b)).
                                        map((monster) =>
                                        <span
                                          key={`${layer.id}:${dungeon.galaxy}:${dungeon.name}:monster:${monster}`}
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            padding: "0.2rem 0.55rem",
                                            borderRadius: "999px",
                                            background: "rgba(255,255,255,0.06)",
                                            fontSize: "0.75rem",
                                            whiteSpace: "nowrap"
                                          }}>
                                          
                                                  {monster}
                                                </span>
                                        )}
                                            {Array.from(
                                          new Set(
                                            dungeon.levels.
                                            filter((level) => level.boss).
                                            map((level) => level.boss).
                                            filter(Boolean)
                                          )
                                        ).
                                        sort((a, b) => a.localeCompare(b)).
                                        map((boss) =>
                                        <span
                                          key={`${layer.id}:${dungeon.galaxy}:${dungeon.name}:boss:${boss}`}
                                          className="dgs-boss-badge"
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            padding: "0.2rem 0.55rem",
                                            borderRadius: "999px",
                                            fontSize: "0.75rem",
                                            whiteSpace: "nowrap"
                                          }}>
                                          
                                                  {boss}
                                                </span>
                                        )}
                                          </> :

                                      <span style={{ fontSize: "0.75rem", opacity: 0.7 }}>
                                            
                                          </span>
                                      }
                                        <span
                                        style={{
                                          marginLeft: "auto",
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "flex-end",
                                          gap: "0.15rem"
                                        }}>
                                        
                                          <button
                                          type="button"
                                          className="back-button"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            toggleFlagDungeon(dungeon);
                                          }}
                                          disabled={flagLoading}
                                          style={{
                                            borderColor: isFlagged ? "#b00020" : undefined,
                                            color: isFlagged ? "#ff6b6b" : undefined
                                          }}
                                          title={isFlagged ? "Unflag this dungeon" : "Flag this dungeon"}>
                                          
                                            {isFlagged ? "Remove Flag" : "Report Inaccurate"}
                                          </button>
                                          {isFlagged &&
                                        <span style={{ fontSize: "0.7rem", opacity: 0.7 }}>
                                              Flagged by: {flaggedMeta?.name || "Unknown"}
                                            </span>
                                        }
                                        </span>
                                      </span>
                                  </summary>
                                  {hasLevels ?
                                  <div className="dgs-route">
                                      {buildDungeonRouteLines(dungeon.levels, dungeon).map((line) =>
                                    <div
                                      key={`${dungeon.name}:${line.tag}:${line.indent}:${line.tokens.map((t) => t.label).join("|")}`}
                                      className={`dgs-route__line${line.indent > 0 ? " dgs-route__line--nested" : ""}`}
                                      style={{
                                        marginLeft: `${line.indent * 18}px`,
                                        paddingLeft: line.indent > 0 ? "22px" : undefined
                                      }}>
                                      
                                          <span className={`dgs-route__tag dgs-route__tag--${line.tag.toLowerCase()}`}>
                                            {line.tag}
                                          </span>
                                          <div className="dgs-route__chain">
                                            {line.tokens.map((token, idx) =>
                                        <React.Fragment key={`${line.tag}:${token.label}:${idx}`}>
                                                <span className="dgs-route__token">
                                                  <button
                                                type="button"
                                                className="dgs-route__token-label-button"
                                                onClick={() =>
                                                openEditModal({
                                                  galaxy: token.galaxy,
                                                  dgCode: token.dgCode,
                                                  dungeonName: token.dungeonName,
                                                  levelLabel: token.levelLabel,
                                                  isBossLevel: token.isBossLevel,
                                                  values: {
                                                    monsterType: token.adds,
                                                    boss: token.boss,
                                                    guards: token.guards
                                                  }
                                                })
                                                }
                                                disabled={!canEditIntel}
                                                title={
                                                canEditIntel ?
                                                token.isBossLevel ?
                                                "Edit boss and guard names" :
                                                "Edit mob name" :
                                                "Only leaders/admin can edit DG intel"
                                                }>

                                                    {token.label}
                                                  </button>
                                                  {token.isBossLevel ?
                                            <span className="dgs-route__token-boss">
                                                      <span
                                                className={`dgs-route__pill dgs-route__pill--boss${
                                                tokenMatchesSearch(token.boss) ? " dgs-route__pill--hit" : ""
                                                }${token.boss ? "" : " dgs-route__pill--empty"}`}>

                                                        {token.boss || "Boss Unknown"}
                                                      </span>
                                              <span className="dgs-route__token-guards">
                                                          <span
                                                  className={`dgs-route__pill dgs-route__pill--guards${
                                                  tokenMatchesSearch(token.guards) ? " dgs-route__pill--hit" : ""
                                                  }${token.guards ? "" : " dgs-route__pill--empty"}`}>

                                                            {token.guards || "Guards Unknown"}
                                                          </span>
                                                        </span>
                                                    </span> :

                                            <span
                                              className={`dgs-route__pill dgs-route__pill--adds${
                                              tokenMatchesSearch(token.adds) ? " dgs-route__pill--hit" : ""
                                              }${token.adds ? "" : " dgs-route__pill--empty"}`}>

                                                      {token.adds || "No mob set"}
                                                    </span>
                                            }
                                                </span>
                                                {idx < line.tokens.length - 1 ?
                                          <span className="dgs-route__sep" aria-hidden="true">
                                                    -&gt;
                                                  </span> :
                                          null}
                                              </React.Fragment>
                                        )}
                                          </div>
                                        </div>
                                    )}
                                    </div> :

                                  <div style={{ padding: "0.5rem 0", opacity: 0.7, fontSize: "0.85rem" }}>
                                      No scan data yet.
                                    </div>
                                  }
                                  </details>);

                            })}
                            </div>
                          </details>);

                    })}
                    </div>
                  }
                </>
                }
            </div>);

          })}
        </div>
      </div>
      {editModalOpen &&
      <div
        className="dgs-route-modal-backdrop"
        role="dialog"
        aria-modal="true"
        onClick={closeEditModal}>
        
          <div
          className="dgs-route-modal"
          onClick={(e) => e.stopPropagation()}>
          
            <div className="dgs-route-modal__title">
              Edit Level {editTarget?.levelLabel || ""}
            </div>
            <div className="dgs-route-modal__message">
              {editTarget?.dungeonName} | {editTarget?.levelLabel}
            </div>
            <div style={{ display: "grid", gap: "0.5rem", margin: "0.75rem 0" }}>
              {editFields.map((field, idx) => {
                const fieldSuggestions = editSuggestions[field] || [];
                const currentValue = String(editTarget?.values?.[field] || "").trim();
                return (
                  <div key={`dg-edit-field:${field}`} style={{ display: "grid", gap: "0.5rem" }}>
                    <div style={{ fontSize: "0.82rem", opacity: 0.8 }}>
                      Current {getEditFieldLabel(field)}:{" "}
                      <strong>{currentValue || "None"}</strong>
                    </div>
                    <label htmlFor={`dg-edit-input-${field}`} style={{ fontSize: "0.85rem", opacity: 0.85 }}>
                      {getEditFieldLabel(field)}
                    </label>
                    <input
                      id={`dg-edit-input-${field}`}
                      value={editValues[field] || ""}
                      onChange={(e) =>
                      setEditValues((prev) => ({
                        ...prev,
                        [field]: e.target.value
                      }))
                      }
                      placeholder={`Enter ${getEditFieldLabel(field).toLowerCase()}...`}
                      autoFocus={idx === 0}
                      style={{
                        width: "80%",
                        display: "block",
                        margin: "0 auto",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid rgba(255,255,255,0.2)",
                        background: "#1b1b1b",
                        color: "var(--textPrimary)"
                      }} />
                    {fieldSuggestions.length > 0 &&
                    <div
                      role="listbox"
                      aria-label={`${getEditFieldLabel(field)} suggestions`}
                      style={{
                        display: "grid",
                        gap: "0.35rem",
                        maxHeight: "180px",
                        overflowY: "auto",
                        padding: "0.5rem",
                        borderRadius: "8px",
                        border: "1px solid rgba(255,255,255,0.12)",
                        background: "rgba(0,0,0,0.2)"
                      }}>
                        {fieldSuggestions.map((suggestion) =>
                      <button
                        key={`dg-edit-suggestion:${field}:${suggestion}`}
                        type="button"
                        className="back-button"
                        style={{ width: "100%", textAlign: "left" }}
                        onClick={() =>
                        setEditValues((prev) => ({
                          ...prev,
                          [field]: suggestion
                        }))
                        }>
                            {suggestion}
                          </button>
                      )}
                      </div>
                    }
                    {field === "guards" &&
                    <div style={{ fontSize: "0.75rem", opacity: 0.75 }}>
                      This will deffo probably maybe work
                    </div>
                    }
                  </div>
                );
              })}
              {editError &&
              <div style={{ color: "#ff6b6b", fontSize: "0.85rem" }}>
                  {editError}
                </div>
              }
            </div>

            <div className="dgs-route-modal__actions">
              <button
              type="button"
              className="back-button"
              onClick={closeEditModal}
              disabled={editSaving}>
              
                Cancel
              </button>
              <button
              type="button"
              className="back-button"
              onClick={handleSaveEdit}
              disabled={editSaving || !canEditIntel || !hasAuthUser}
              title={!hasAuthUser ? "Sign in required" : !canEditIntel ? "Leader/admin only" : "Save edit"}>
              
                {editSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      }
      {routeModalOpen &&
      <div
        className="dgs-route-modal-backdrop"
        role="dialog"
        aria-modal="true"
        onClick={() => {
          if (!routeGenerating) setRouteModalOpen(false);
        }}>
        
          <div
          className="dgs-route-modal"
          onClick={(e) => e.stopPropagation()}>
          
            <div className="dgs-route-modal__title">
              {routeGenerating ? "Generating Route" : routeError ? "Route Error" : "Route Calculated"}
            </div>
            <div className="dgs-route-modal__message">
              {routeGenerating ?
            routeProgress.message :
            routeError ?
            routeError :
            ""}
            </div>
            <div className="dgs-route-progress" aria-label="Route generation progress">
              <div
              className="dgs-route-progress__bar"
              style={{ width: `${routePercent}%` }} />
            
            </div>

            {routeResult && !routeGenerating && !routeError &&
          <>
                <div className="dgs-route-modal__meta">Total jumps: {routeResult.totalJumps}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", margin: "0.75rem 0" }}>
                  <select
                value={routeStartGalaxy}
                onChange={(e) => setRouteStartGalaxy(e.target.value)}
                style={{
                  flex: "1 1 240px",
                  padding: "8px 10px",
                  borderRadius: "6px",
                  border: "1px solid rgba(255,255,255,0.2)",
                  background: "#1b1b1b",
                  color: "var(--textPrimary)"
                }}>

                    <option value="" disabled>
                      Select start galaxy...
                    </option>
                    {routeGalaxyOptions.map((name) =>
                <option key={`route-start:${name}`} value={name}>
                        {name}
                      </option>
                )}
                  </select>
                  <button
                type="button"
                className="back-button"
                onClick={handleRecalculateFromStart}
                disabled={routeGenerating || !routeStartGalaxy}
                title={!routeStartGalaxy ? "Choose a start galaxy" : "Recalculate route"}>

                    Change Start Point
                  </button>
                </div>
                <ol className="dgs-route-modal__list">
                  {routeResult.order.map((name) =>
              <li key={name}>{name}</li>
              )}
                </ol>
              </>
          }

            <div className="dgs-route-modal__actions">
              <button
              type="button"
              className="back-button"
              onClick={() => setRouteModalOpen(false)}
              disabled={routeGenerating}>
              
                Close
              </button>
            </div>
          </div>
        </div>
      }
    </div>);

};

export default TeamDGs;
